import classNames from "classnames";
import { FC, ReactElement, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router";

import { usePageviewTracker } from "./analytics/SiteImprove";
import { useSiteImprove } from "./analytics/useSiteImprove";
import styles from "./App.module.css";
import BehandlerFlowConfirmation from "./finish/BehandlerFlowConfirmation";
import BehandlerFlowType2Confirmation from "./finish/BehandlerFlowType2Confirmation";
import ManuellFlowConfirmation from "./finish/ManuellConfirm";
import ManuellFlowConfirmationSignering from "./finish/ManuellConfirmSignering";
import { RegistrationCompleted } from "./finish/RegistrationCompleted";
import SigneringFlowConfirmation from "./finish/SigneringFlowConfirmation";
import Header from "./header/Header";
import { linkExist } from "./infrastructure/linkUtils";
import { RedirectToCorrectForm } from "./infrastructure/redirect/RedirectToCorrectForm";
import type { Dispatch } from "./infrastructure/redux/actions";
import {
  fetchEntrypointAction,
  setControlFlow,
} from "./infrastructure/redux/app/app-actions";
import type { ReduxState } from "./infrastructure/redux/state";
import Translated from "./infrastructure/Translated";
import { useRootPath } from "./infrastructure/useRootPath";
import { useTranslate } from "./infrastructure/useTranslate";
import Login from "./login/Login";
import LoginSignering from "./login/LoginSignering";
import Manuell from "./manuell/Manuell";
import { ManuellSkjema } from "./manuell/ManuellSkjema";
import { ManuellSkjemaSignering } from "./manuell/ManuellSkjemaSignering";
import { UnderkontoSkjema } from "./manuell/UnderkontoSkjema";
import { Bedrift } from "./organisation/Bedrift";
import { NotRegistered } from "./person/not-registered/NotRegistered";
import { Person } from "./person/Person";
import { PersonForm } from "./person/PersonForm";
import { PersonGjenapne } from "./person/PersonGjenapne";
import { Fullmakt } from "./registrer/Fullmakt";
import { OrganisationAccountDetails } from "./registrer/OrganisationAccountDetails";
import Registrering from "./registrer/Registrer";
import Terms from "./registrer/terms/Terms";
import StatusPageBanner from "./statusbanner/StatusPageBanner";

interface RequireAuthProps {
  children: ReactElement;
  acceptedAuth: string;
  redirectTo: string;
}

const RequireAuth: FC<RequireAuthProps> = ({
  children,
  acceptedAuth,
  redirectTo,
}) => {
  const currentAuth = useSelector((state: ReduxState) => state.user.auth);

  const isAuthenticated = currentAuth === acceptedAuth;

  return isAuthenticated ? children : <Navigate to={redirectTo} />;
};

const App: FC = () => {
  const loadingDone = useSelector((state: ReduxState) =>
    linkExist("session", state.link),
  );
  const isSigneringContext = useSelector(
    (state: ReduxState) => state.app.params.mode === "signering",
  );

  useSiteImprove(true);

  const rootPath = useRootPath();

  const location = useLocation();

  const dispatch = useDispatch<Dispatch>();

  const controlFlowCalled = useRef(false);

  useEffect(() => {
    if (!controlFlowCalled.current) {
      dispatch(setControlFlow());
      controlFlowCalled.current = true;
    }
  }, [dispatch]);

  const fetchEntrypoint = useCallback(
    () => dispatch(fetchEntrypointAction()),
    [dispatch],
  );

  useEffect(() => {
    fetchEntrypoint();
  }, [fetchEntrypoint]);

  usePageviewTracker();
  const translate = useTranslate();

  if (!loadingDone) {
    return null;
  }

  const bedriftIntroUrl = `${rootPath}/bedrift?logout=1`;
  const personIntroUrl = `${rootPath}/person`;

  return (
    <>
      <StatusPageBanner />
      <Header />
      <article
        className={classNames(
          styles.appArticle,
          location.pathname.endsWith("/sconfirm") &&
            styles.wideSigneringConfirmArticle,
        )}
      >
        <Routes>
          <Route path={"registrering/:language?"}>
            <Route path="bedrift" element={<Bedrift />}>
              <Route
                index={true}
                element={<Navigate to="login" replace={true} />}
              />
              <Route
                path="login"
                element={isSigneringContext ? <LoginSignering /> : <Login />}
              />
              <Route path="registrer">
                <Route
                  index={true}
                  element={
                    <RequireAuth
                      acceptedAuth={"REGISTRATION"}
                      redirectTo={bedriftIntroUrl}
                    >
                      <Registrering />
                    </RequireAuth>
                  }
                />

                <Route
                  path="fullmakt"
                  element={
                    <RequireAuth
                      acceptedAuth={"REGISTRATION"}
                      redirectTo={bedriftIntroUrl}
                    >
                      <Fullmakt isManuellRegistrering={false} />
                    </RequireAuth>
                  }
                />
                <Route
                  path="avtalevilkaar"
                  element={
                    <RequireAuth
                      acceptedAuth={"REGISTRATION"}
                      redirectTo={bedriftIntroUrl}
                    >
                      <Terms />
                    </RequireAuth>
                  }
                />

                <Route
                  path="kontodetaljer"
                  element={
                    <RequireAuth
                      acceptedAuth={"REGISTRATION"}
                      redirectTo={bedriftIntroUrl}
                    >
                      <OrganisationAccountDetails />
                    </RequireAuth>
                  }
                />
              </Route>
              <Route path="sconfirm" element={<SigneringFlowConfirmation />} />
              <Route
                path="i2confirm"
                element={<BehandlerFlowType2Confirmation />}
              />
              <Route
                path="manuell_confirm"
                element={
                  isSigneringContext ? (
                    <ManuellFlowConfirmationSignering />
                  ) : (
                    <ManuellFlowConfirmation />
                  )
                }
              />
              <Route path="iconfirm" element={<BehandlerFlowConfirmation />} />
              <Route path="completed" element={<RegistrationCompleted />} />
              <Route path="manuell">
                <Route
                  index={true}
                  element={
                    <RequireAuth
                      acceptedAuth={"REGISTRATION"}
                      redirectTo={bedriftIntroUrl}
                    >
                      {isSigneringContext ? (
                        <Manuell
                          documentTitle={`Signering - ${translate(
                            "register.org.insufficient_access.header",
                          )}`}
                          criteria={
                            <Translated
                              textKey="register.org.insufficient_access.criteria_signering"
                              allowHtml={true}
                            />
                          }
                        />
                      ) : (
                        <Manuell
                          documentTitle={`Digipost - ${translate(
                            "register.org.insufficient_access.header",
                          )}`}
                          criteria={
                            <Translated
                              textKey="register.org.insufficient_access.criteria"
                              allowHtml={true}
                            />
                          }
                        />
                      )}
                    </RequireAuth>
                  }
                />
                <Route
                  path="fullmakt"
                  element={
                    <RequireAuth
                      acceptedAuth={"REGISTRATION"}
                      redirectTo={bedriftIntroUrl}
                    >
                      <Fullmakt isManuellRegistrering={true} />
                    </RequireAuth>
                  }
                />
                <Route path="avtalevilkaar" element={<Terms />} />
                <Route
                  path="skjema"
                  element={
                    <RequireAuth
                      acceptedAuth={"REGISTRATION"}
                      redirectTo={bedriftIntroUrl}
                    >
                      {isSigneringContext ? (
                        <ManuellSkjemaSignering />
                      ) : (
                        <ManuellSkjema />
                      )}
                    </RequireAuth>
                  }
                />
              </Route>
            </Route>
            <Route path="underkonto">
              <Route path="feil" element={<Navigate to={"/bedrift"} />} />
              <Route
                path=":accountId"
                element={
                  <RequireAuth
                    acceptedAuth={"CORPORATION"}
                    redirectTo={`${rootPath}/underkonto/feil`}
                  >
                    <UnderkontoSkjema />
                  </RequireAuth>
                }
              />
            </Route>
            <Route path="person">
              <Route index={true} element={<Person />} />
              <Route path="not-registered" element={<NotRegistered />} />
              <Route
                path="form"
                element={
                  <RequireAuth
                    acceptedAuth={"REGISTRATION"}
                    redirectTo={personIntroUrl}
                  >
                    <PersonForm />
                  </RequireAuth>
                }
              />
              <Route
                path="gjenapne"
                element={
                  <RequireAuth
                    acceptedAuth={"REGISTRATION"}
                    redirectTo={personIntroUrl}
                  >
                    <PersonGjenapne />
                  </RequireAuth>
                }
              />
            </Route>
            <Route
              element={<Navigate to={`${rootPath}/bedrift/registrer`} />}
            />
            <Route index={true} element={<RedirectToCorrectForm />} />
          </Route>
        </Routes>
      </article>
    </>
  );
};

export default App;
